import type { ComputedRef } from 'vue';
import { computed } from 'vue';

import {
  BASE_CSS,
  HEIGHT_CSS,
  ICON_SIZE,
  PADDING_CSS,
  ROUNDED_CSS,
  STACKED_GAP_CSS,
  STACKED_ICON_WRAPPER_BASE,
  STACKED_ICON_WRAPPER_SIZE_CSS,
  STACKED_LABEL_SIZE_CSS,
  STACKED_LABEL_WIDTH_CSS,
  STACKED_LAYOUT_CSS,
  STACKED_LOOKS,
  TEXT_SIZE_CSS,
  UNDERLINE_CSS,
  WIDTH_HEIGHT_CSS,
} from './sparkButtonConstants';
import type { ColorScheme, SparkButtonProps } from './types';

export function useSparkButton(
  look: ComputedRef<string>,
  color: ComputedRef<SparkButtonProps['color']>,
  shape: ComputedRef<SparkButtonProps['shape']>,
  size: ComputedRef<SparkButtonProps['size']>,
  disabled: ComputedRef<SparkButtonProps['disabled']>,
  colorSchemes: Record<string, ColorScheme>,
) {
  const isStacked = computed(() =>
    (STACKED_LOOKS as readonly string[]).includes(look.value),
  );

  const roundedCss = computed(() => {
    return ROUNDED_CSS[shape.value][size.value];
  });

  const colorScheme = computed(() => {
    return colorSchemes[look.value];
  });

  const resolvedBg = computed<string>(() => {
    if (!colorScheme.value) {
      return '';
    }
    return disabled.value
      ? (colorScheme.value.backgroundDisabled?.[color.value] ?? '')
      : (colorScheme.value.background?.[color.value] ?? '');
  });

  const bgCss = computed<string>(() => {
    if (isStacked.value) {
      return '';
    }
    return resolvedBg.value;
  });

  const borderCss = computed<string>(() => {
    if (!colorScheme.value) {
      return '';
    }
    return disabled.value
      ? (colorScheme.value.borderDisabled?.[color.value] ?? '')
      : (colorScheme.value.border?.[color.value] ?? '');
  });

  const textCss = computed<string>(() => {
    if (!colorScheme.value) {
      return '';
    }
    return disabled.value
      ? (colorScheme.value.textDisabled?.[color.value] ?? '')
      : (colorScheme.value.text?.[color.value] ?? '');
  });

  const paddingCss = computed(() => {
    if (shape.value === 'circle' || isStacked.value) {
      return '';
    }
    return PADDING_CSS[shape.value]?.[size.value] ?? '';
  });

  const heightCss = computed(() => {
    if (shape.value === 'circle' || isStacked.value) {
      return '';
    }
    return HEIGHT_CSS[shape.value]?.[size.value] ?? '';
  });

  const textSizeCss = computed(() => {
    if (isStacked.value) {
      return STACKED_LABEL_SIZE_CSS[size.value];
    }
    if (shape.value === 'circle') {
      return '';
    }
    return TEXT_SIZE_CSS[shape.value]?.[size.value] ?? '';
  });

  const widthHeightCss = computed(() => {
    if (shape.value === 'pill' || shape.value === 'rounded') {
      return '';
    }
    return WIDTH_HEIGHT_CSS[shape.value]?.[size.value] ?? '';
  });

  const underlineCss = computed(() => {
    return disabled.value ? '' : (UNDERLINE_CSS?.[look.value] ?? '');
  });

  const gapCss = computed(() => {
    if (isStacked.value) {
      return STACKED_GAP_CSS[size.value];
    }
    return size.value === 'xs' ? 'gap-x-1' : 'gap-x-2';
  });

  const layoutCss = computed(() => (isStacked.value ? STACKED_LAYOUT_CSS : ''));

  const labelWidthCss = computed(() =>
    isStacked.value ? STACKED_LABEL_WIDTH_CSS[size.value] : '',
  );

  const iconWrapperCss = computed(() => {
    if (!isStacked.value) {
      return '';
    }
    const iconColor = disabled.value
      ? (colorScheme.value?.textDisabled?.[color.value] ?? '')
      : (colorScheme.value?.iconColor?.[color.value] ?? '');
    return [
      STACKED_ICON_WRAPPER_BASE,
      STACKED_ICON_WRAPPER_SIZE_CSS[size.value],
      resolvedBg.value,
      iconColor,
    ]
      .filter((x) => x)
      .join(' ');
  });

  const buttonCss = computed<string[]>(() => {
    const result = [
      BASE_CSS,
      bgCss.value,
      borderCss.value,
      textCss.value,
      paddingCss.value,
      roundedCss.value,
      textSizeCss.value,
      widthHeightCss.value,
      underlineCss.value,
      gapCss.value,
      heightCss.value,
      layoutCss.value,
    ].filter((x) => x);

    if (disabled.value) {
      result.push('cursor-not-allowed');
    }

    return result;
  });

  const iconSize = computed(() => ICON_SIZE[size.value]);

  const style = computed(() => ({
    '--radiate-color': colorScheme.value?.radiateColor?.[color.value] ?? '',
  }));
  return {
    buttonCss,
    isStacked,
    iconWrapperCss,
    labelWidthCss,

    // for custom button
    iconSize,
    roundedCss,
    baseCss: BASE_CSS,
    paddingCss,
    textSizeCss,
    style,
  };
}
